/**
 * Market Domination — automatic threshold-based alerts.
 * Emits via trackEvent so the existing audit/posthog pipelines pick them up.
 * No new tables. No mocks. Strictly derived from local engine snapshot.
 */
import { trackEvent } from "@/lib/audit";
import {
  getAllCountryMetrics,
  getInfluencerRanking,
  getViralitySummary,
  subscribe,
} from "./marketScoreEngine";
import type { CountryCode } from "@/lib/country/countryExperienceEngine";

export type MarketAlertKind =
  | "score_above_80"
  | "growth_drop_20"
  | "virality_leader_changed"
  | "retention_alert"
  | "influencer_breakout"
  | "conversion_dropoff";

export interface MarketAlert {
  id: string;
  kind: MarketAlertKind;
  country?: CountryCode;
  slug?: string;
  message: string;
  at: number;
}

const STATE_KEY = "vs:market_alerts_state_v1";
const LIST_KEY = "vs:market_alerts_list_v1";
const MAX_ALERTS = 50;

type AlertState = {
  lastViralLeader?: CountryCode;
  alerted?: Record<string, number>;
};

function readState(): AlertState {
  if (typeof window === "undefined") return {};
  try { return JSON.parse(window.localStorage.getItem(STATE_KEY) ?? "{}") as AlertState; }
  catch { return {}; }
}
function writeState(s: AlertState) {
  try { window.localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch { /* noop */ }
}

export function listAlerts(): MarketAlert[] {
  if (typeof window === "undefined") return [];
  try { return (JSON.parse(window.localStorage.getItem(LIST_KEY) ?? "[]") as MarketAlert[]); }
  catch { return []; }
}

function pushAlert(a: MarketAlert) {
  const list = listAlerts();
  list.unshift(a);
  try {
    window.localStorage.setItem(LIST_KEY, JSON.stringify(list.slice(0, MAX_ALERTS)));
  } catch { /* noop */ }
}

const COOLDOWN_MS = 60 * 60 * 1000;
function recentlyAlerted(state: AlertState, key: string): boolean {
  return !!state.alerted?.[key] && Date.now() - (state.alerted[key] ?? 0) < COOLDOWN_MS;
}
function markAlerted(state: AlertState, key: string) {
  state.alerted ??= {};
  state.alerted[key] = Date.now();
}

function emit(kind: MarketAlertKind, message: string, extra: Record<string, unknown> = {}) {
  const alert: MarketAlert = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind, message, at: Date.now(),
    country: (extra.country as CountryCode | undefined),
    slug: (extra.slug as string | undefined),
  };
  pushAlert(alert);
  void trackEvent("market_alert_emitted", { kind, message, ...extra });
}

export function runMarketAlerts() {
  const state = readState();
  const metrics = getAllCountryMetrics();
  const virality = getViralitySummary();

  for (const m of metrics) {
    if (m.score >= 80) {
      const k = `score80:${m.country}`;
      if (!recentlyAlerted(state, k)) {
        emit("score_above_80",
          `${m.country} supera score 80 (${m.score}).`,
          { country: m.country, score: m.score });
        markAlerted(state, k);
      }
    }
    if (m.growth.d24 <= -20) {
      const k = `drop20:${m.country}`;
      if (!recentlyAlerted(state, k)) {
        emit("growth_drop_20",
          `${m.country} cae ${m.growth.d24}% en las últimas 24h.`,
          { country: m.country, drop: m.growth.d24 });
        markAlerted(state, k);
      }
    }
    if (m.retention.d7 < 15 && m.registered7d >= 10) {
      const k = `ret15:${m.country}`;
      if (!recentlyAlerted(state, k)) {
        emit("retention_alert",
          `Retención D7 de ${m.country} baja a ${m.retention.d7}%.`,
          { country: m.country, d7: m.retention.d7 });
        markAlerted(state, k);
      }
    }
  }

  const leader = virality[0];
  if (leader && leader.kFactor >= 0.2 && state.lastViralLeader !== leader.country) {
    emit("virality_leader_changed",
      `${leader.country} se convierte en líder viral (k=${leader.kFactor}).`,
      { country: leader.country, kFactor: leader.kFactor });
    state.lastViralLeader = leader.country;
  }

  for (const inf of getInfluencerRanking(null, 5)) {
    if (inf.signups >= 100) {
      const k = `infl100:${inf.slug}`;
      if (!recentlyAlerted(state, k)) {
        emit("influencer_breakout",
          `${inf.displayName} (${inf.country}) generó ${inf.signups} registros.`,
          { country: inf.country, slug: inf.slug, signups: inf.signups });
        markAlerted(state, k);
      }
    }
  }

  writeState(state);
}

let started = false;
export function startMarketAlerts() {
  if (started || typeof window === "undefined") return;
  started = true;
  runMarketAlerts();
  const tick = () => runMarketAlerts();
  subscribe(tick);
  window.setInterval(tick, 60_000);
}
